import React, { useEffect, useState } from 'react';

import { useSnackbar } from 'notistack';
import { useParams } from 'react-router-dom';

import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Stack,
  TextField,
  Tooltip,
} from '@mui/material';

import PageHeader from '~/components/Common/Elements/PageHeader';

import { useGetBundle, useUpdateBundle } from '~/hooks/apis/bundle/useBundles';

import { BUNDLE_STATUS_LABELS } from '~/types/bundles/bundle';

import BundleTourSection from '../../Bundles/BundleTourSection';
import { BUNDLES_PATH } from '../../nativeTourModel';

export default function TourBundlePage() {
  const { bundle_id: bundleId } = useParams<{ bundle_id: string }>();
  const { enqueueSnackbar } = useSnackbar();

  const [name, setName] = useState('');
  const [isApproveOpen, setIsApproveOpen] = useState(false);

  const { data: bundle, isLoading, error } = useGetBundle(bundleId);
  const updateBundle = useUpdateBundle(bundleId);

  useEffect(() => {
    if (bundle) {
      setName(bundle.name);
    }
  }, [bundle]);

  const approveBlocker = !bundle
    ? undefined
    : bundle.components.length === 0
      ? 'Add a tour before approving'
      : bundle.departure_dates.length === 0
        ? 'Choose departure dates before approving'
        : undefined;

  const trimmedName = name.trim();
  const isNameChanged = !!bundle && !!trimmedName && trimmedName !== bundle.name;

  const handleSaveName = async (event: React.FormEvent) => {
    event.preventDefault();
    try {
      await updateBundle.mutateAsync({ name: trimmedName });
      enqueueSnackbar('Bundle renamed', { variant: 'success' });
    } catch (err) {
      enqueueSnackbar(err.message, { variant: 'error' });
    }
  };

  const handleStatusChange = async (status: 'draft' | 'approved') => {
    try {
      await updateBundle.mutateAsync({ status });
      setIsApproveOpen(false);
      enqueueSnackbar(`Bundle ${status === 'approved' ? 'approved' : 'moved back to draft'}`, { variant: 'success' });
    } catch (err) {
      enqueueSnackbar(err.message, { variant: 'error' });
    }
  };

  return (
    <Container>
      <PageHeader
        title={bundle?.name ?? 'Bundle'}
        backButton={BUNDLES_PATH}
        titleActions={
          bundle && (
            <Chip
              size="small"
              label={BUNDLE_STATUS_LABELS[bundle.status]}
              color={bundle.status === 'approved' ? 'success' : 'default'}
            />
          )
        }
      >
        <Box pb={1}>
          {bundle?.status === 'draft' && (
            <Tooltip title={approveBlocker ?? ''}>
              {/* Disabled buttons fire no events, so the tooltip needs a wrapper. */}
              <span>
                <Button
                  variant="contained"
                  onClick={() => setIsApproveOpen(true)}
                  disabled={!!approveBlocker || updateBundle.isPending}
                >
                  Approve
                </Button>
              </span>
            </Tooltip>
          )}
          {bundle?.status === 'approved' && (
            <Button onClick={() => handleStatusChange('draft')} disabled={updateBundle.isPending}>
              Move back to draft
            </Button>
          )}
        </Box>
      </PageHeader>

      {isLoading && (
        <Box p={4} display="flex" justifyContent="center">
          <CircularProgress />
        </Box>
      )}

      {!!error && <Alert severity="error">{error.message}</Alert>}

      {!!bundle && (
        <Stack spacing={4}>
          <form onSubmit={handleSaveName}>
            <Stack direction="row" spacing={2} alignItems="flex-start">
              <TextField
                label="Name"
                value={name}
                onChange={(event) => setName(event.target.value)}
                inputProps={{ maxLength: 255 }}
                required
                fullWidth
              />
              <Button type="submit" variant="contained" disabled={!isNameChanged || updateBundle.isPending}>
                Save
              </Button>
            </Stack>
          </form>

          <BundleTourSection bundle={bundle} />
        </Stack>
      )}

      <Dialog open={isApproveOpen} onClose={() => setIsApproveOpen(false)}>
        <DialogTitle>Approve bundle?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Components and departure dates can't be changed once the bundle is approved. Price adjustments still can.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIsApproveOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={() => handleStatusChange('approved')} disabled={updateBundle.isPending}>
            Approve
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}
